import React, { useEffect, useCallback, useState } from 'react';
import { Badge, Popover, Button, List, Typography, Space, Tag, Tooltip, Divider } from 'antd';
import { BellOutlined, CheckOutlined, HistoryOutlined, DownOutlined, UpOutlined, ArrowRightOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  fetchNotifications,
  fetchUnreadCount,
  markNotificationRead,
  markAllNotificationsRead,
} from '../../store/notificationSlice';

const { Text } = Typography;

const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;

const EVENT_TYPE_LABELS = {
  interview_requested: { label: 'Interview Requested', color: 'blue' },
  interview_accepted: { label: 'Interview Accepted', color: 'green' },
  interview_declined: { label: 'Interview Declined', color: 'red' },
  application_rejected: { label: 'Profile Rejected', color: 'volcano' },
  talent_hired: { label: 'Talent Hired', color: 'purple' },
  timesheet_approved: { label: 'Timesheet Approved', color: 'green' },
  timesheet_rejected: { label: 'Timesheet Rejected', color: 'red' },
  invoice_approved: { label: 'Invoice Approved', color: 'cyan' },
  invoice_paid: { label: 'Invoice Paid', color: 'gold' },
  profile_submitted: { label: 'Profile Submitted', color: 'geekblue' },
  interview_cancelled: { label: 'Interview Cancelled', color: 'red' },
  talent_pending_hire: { label: 'Talent Approved for Hire', color: 'purple' },
  compliance_docs_generated: { label: 'Compliance Docs Ready', color: 'orange' },
  compliance_docs_all_signed: { label: 'Compliance Docs Signed', color: 'green' },
  feedback_submitted: { label: 'Performance Feedback', color: 'orange' },
  feedback_response: { label: 'Feedback Response', color: 'geekblue' },
};

const formatTimeAgo = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const diff = Date.now() - date.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
};

const NotificationItem = ({ item, onMarkRead, onNavigate }) => {
  const isUnread = !item.readAt;
  const meta = EVENT_TYPE_LABELS[item.eventType] || { label: item.eventType, color: 'default' };
  const hasAction = !!item.actionUrl;

  const handleActionClick = (e) => {
    e.stopPropagation();
    if (isUnread) onMarkRead(item.id);
    if (item.actionUrl) onNavigate(item.actionUrl);
  };

  return (
    <List.Item
      key={item.id}
      style={{
        padding: '8px 4px',
        background: isUnread ? '#f6ffff' : 'transparent',
        borderRadius: 6,
        cursor: isUnread ? 'pointer' : 'default',
        alignItems: 'flex-start',
      }}
      onClick={() => isUnread && !hasAction && onMarkRead(item.id)}
    >
      <div style={{ width: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
          <Space size={4} wrap>
            <Tag color={meta.color} style={{ margin: 0, fontSize: 11, lineHeight: '18px' }}>
              {meta.label}
            </Tag>
            {isUnread && (
              <span
                style={{
                  display: 'inline-block',
                  width: 7,
                  height: 7,
                  borderRadius: '50%',
                  background: '#00d9a9',
                  marginTop: 5,
                }}
              />
            )}
          </Space>
          <Text type="secondary" style={{ fontSize: 11, whiteSpace: 'nowrap', flexShrink: 0 }}>
            {formatTimeAgo(item.createdAt)}
          </Text>
        </div>
        <Text
          style={{
            fontSize: 12,
            display: 'block',
            marginTop: 4,
            color: isUnread ? '#000' : '#595959',
            fontWeight: isUnread ? 500 : 400,
          }}
        >
          {item.body}
        </Text>
        {hasAction && (
          <div style={{ marginTop: 6 }}>
            <Button
              type="link"
              size="small"
              icon={<ArrowRightOutlined />}
              iconPosition="end"
              onClick={handleActionClick}
              style={{
                padding: 0,
                fontSize: 12,
                height: 'auto',
                color: '#014c75',
                fontWeight: 600,
              }}
            >
              View
            </Button>
          </div>
        )}
      </div>
    </List.Item>
  );
};

const NotificationBell = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { notifications, unreadCount, loading } = useSelector((state) => state.notifications);
  const [open, setOpen] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  const now = Date.now();
  const recentNotifications = notifications.filter(
    (n) => now - new Date(n.createdAt).getTime() <= THIRTY_DAYS_MS
  );
  const historyNotifications = notifications.filter(
    (n) => now - new Date(n.createdAt).getTime() > THIRTY_DAYS_MS
  );

  const loadUnreadCount = useCallback(() => {
    const token = localStorage.getItem('authToken');
    if (token) {
      dispatch(fetchUnreadCount());
    }
  }, [dispatch]);

  useEffect(() => {
    loadUnreadCount();
    const interval = setInterval(loadUnreadCount, 60000);
    return () => clearInterval(interval);
  }, [loadUnreadCount]);

  const handleOpenChange = (visible) => {
    setOpen(visible);
    if (visible) {
      dispatch(fetchNotifications(200));
    }
    if (!visible) {
      setShowHistory(false);
    }
  };

  const handleMarkRead = (id) => {
    dispatch(markNotificationRead(id));
  };

  const handleMarkAllRead = () => {
    dispatch(markAllNotificationsRead());
  };

  const handleNavigate = (url) => {
    setOpen(false);
    navigate(url);
  };

  const popoverContent = (
    <div style={{ width: 360, maxHeight: 520, display: 'flex', flexDirection: 'column' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '8px 4px',
          borderBottom: '1px solid #f0f0f0',
          marginBottom: 4,
        }}
      >
        <Text strong style={{ fontSize: 14 }}>
          Notifications
        </Text>
        {unreadCount > 0 && (
          <Button
            type="link"
            size="small"
            icon={<CheckOutlined />}
            onClick={handleMarkAllRead}
            style={{ padding: 0, fontSize: 12 }}
          >
            Mark all read
          </Button>
        )}
      </div>

      <div style={{ overflowY: 'auto', flex: 1 }}>
        {recentNotifications.length === 0 && !loading ? (
          <div style={{ padding: '24px 0', textAlign: 'center' }}>
            <Text type="secondary">No recent notifications</Text>
          </div>
        ) : (
          <List
            loading={loading}
            dataSource={recentNotifications}
            renderItem={(item) => (
              <NotificationItem
                key={item.id}
                item={item}
                onMarkRead={handleMarkRead}
                onNavigate={handleNavigate}
              />
            )}
          />
        )}

        {historyNotifications.length > 0 && (
          <>
            <Divider style={{ margin: '4px 0' }}>
              <Button
                type="text"
                size="small"
                icon={showHistory ? <UpOutlined /> : <DownOutlined />}
                iconPosition="end"
                onClick={() => setShowHistory((prev) => !prev)}
                style={{ fontSize: 12, color: '#8c8c8c', padding: '0 4px' }}
              >
                <HistoryOutlined style={{ marginRight: 4 }} />
                History ({historyNotifications.length})
              </Button>
            </Divider>

            {showHistory && (
              <div style={{ opacity: 0.85 }}>
                <List
                  dataSource={historyNotifications}
                  renderItem={(item) => (
                    <NotificationItem
                      key={item.id}
                      item={item}
                      onMarkRead={handleMarkRead}
                      onNavigate={handleNavigate}
                    />
                  )}
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );

  return (
    <Popover
      content={popoverContent}
      trigger="click"
      open={open}
      onOpenChange={handleOpenChange}
      placement="bottomRight"
      overlayStyle={{ padding: 0 }}
      overlayInnerStyle={{ padding: '8px 12px' }}
    >
      <Tooltip title="Notifications">
        <Badge
          count={unreadCount}
          size="small"
          overflowCount={99}
          style={{ backgroundColor: '#00d9a9' }}
        >
          <BellOutlined
            style={{
              fontSize: 20,
              color: '#fff',
              cursor: 'pointer',
              padding: '4px 8px',
            }}
          />
        </Badge>
      </Tooltip>
    </Popover>
  );
};

export default NotificationBell;
